import { useState } from "react";
import type { Supplier } from "../../services/suppliersApi";
import { useUpdateSupplier } from "../../hooks/useSuppliers";
import { useConfirm, withConfirmSuccess } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Pencil } from "lucide-react";
import { useCounties } from "@/shared/hooks/useMetaData";

const COUNTIES = [
  "Nairobi",
  "Kiambu",
  "Kimbimbi",
  "Nakuru",
  "Uasin Gishu",
  "Kisii",
  "Nyeri",
];

const PAYMENT_METHODS = [
  "Cash",
  "Bank Transfer",
  "Mobile Money",
  "Check",
  "Credit",
];

interface EditSupplierDialogProps {
  supplier: Supplier;
}

export const EditSupplierDialog = ({ supplier }: EditSupplierDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: supplier.id,
    supplierName: supplier.supplierName,
    contactPerson: supplier.contactPerson || "",
    phoneNumber: supplier.phoneNumber || "",
    email: supplier.email || "",
    address: supplier.address || "",
    county: supplier.county || "",
    town: supplier.town || "",
    taxId: supplier.taxId || "",
    bankAccount: supplier.bankAccount || "",
    mobileMoneyNumber: supplier.mobileMoneyNumber || "",
    paymentMethod: supplier.paymentMethod || "",
    creditLimit: supplier.creditLimit || 0,
    creditPeriodDays: supplier.creditPeriodDays || 0,
    leadTimeDays: supplier.leadTimeDays || 0,
    isActive: supplier.isActive,
    contractStartDate: supplier.contractStartDate
      ? supplier.contractStartDate.split("T")[0]
      : "",
    contractEndDate: supplier.contractEndDate
      ? supplier.contractEndDate.split("T")[0]
      : "",
    notes: "",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const updateMutation = useUpdateSupplier(supplier.id);
  const countiesQuery = useCounties();
  const confirm = useConfirm();

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    confirm({
      title: "Save supplier changes?",
      description: `Changes to “${supplier.supplierName}” will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        withConfirmSuccess(
          updateMutation.mutateAsync(formData as any),
          "Supplier updated"
        )
          .then(() => setOpen(false))
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[95vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]" >
        <DialogHeader>
          <DialogTitle>Edit Supplier</DialogTitle>
          <DialogDescription>Update supplier information</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {updateMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {updateMutation.error.message}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="supplierName">
                  Supplier Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="supplierName"
                  value={formData.supplierName}
                  onChange={(e) => handleChange("supplierName", e.target.value)}
                  required
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="contactPerson">
                  Contact Person <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) =>
                    handleChange("contactPerson", e.target.value)
                  }
                  required
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="taxId">
                  Tax ID <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="taxId"
                  value={formData.taxId}
                  onChange={(e) => handleChange("taxId", e.target.value)}
                  required
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="isActive">Status</FieldLabel>
                <div className="flex items-center space-x-2 mt-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                    disabled={updateMutation.isPending}
                    className="h-4 w-4"
                  />
                  <label htmlFor="isActive" className="text-sm cursor-pointer">
                    Active
                  </label>
                </div>
              </Field>
            </div>
          </div>

          {/* Contact Information (abbreviated for space) */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="phoneNumber">Phone Number</FieldLabel>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleChange("phoneNumber", e.target.value)}
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="address">Address</FieldLabel>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="county">
                  County <span className="text-red-500">*</span>
                </FieldLabel>
                {countiesQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <select
                    id="county"
                    value={formData.county}
                    onChange={(e) => handleChange("county", e.target.value)}
                    required
                    disabled={updateMutation.isPending}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select County</option>
                    {countiesQuery.data?.map((county) => (
                      <option key={county.code} value={county.name}>
                        {county.name}
                      </option>
                    ))}
                  </select>
                )}
                {fieldErrors.county && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.county.join(", ")}
                  </div>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="town">Town</FieldLabel>
                <Input
                  id="town"
                  value={formData.town}
                  onChange={(e) => handleChange("town", e.target.value)}
                  disabled={updateMutation.isPending}
                />
              </Field>
            </div>
          </div>

          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Financial Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="paymentMethod">Payment Method</FieldLabel>
                <select
                  id="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={(e) =>
                    handleChange("paymentMethod", e.target.value)
                  }
                  disabled={updateMutation.isPending}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select Payment Method</option>
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <FieldLabel htmlFor="creditLimit">Credit Limit</FieldLabel>
                <Input
                  id="creditLimit"
                  type="number"
                  value={formData.creditLimit}
                  onChange={(e) =>
                    handleChange("creditLimit", parseFloat(e.target.value) || 0)
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="creditPeriodDays">
                  Credit Period Days
                </FieldLabel>
                <Input
                  id="creditPeriodDays"
                  type="number"
                  value={formData.creditPeriodDays}
                  onChange={(e) =>
                    handleChange(
                      "creditPeriodDays",
                      parseInt(e.target.value) || 0,
                    )
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="leadTimeDays">Lead Time Days</FieldLabel>
                <Input
                  id="leadTimeDays"
                  type="number"
                  value={formData.leadTimeDays}
                  onChange={(e) =>
                    handleChange("leadTimeDays", parseInt(e.target.value) || 0)
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="bankAccount">Bank Account</FieldLabel>
                <Input
                  id="bankAccount"
                  value={formData.bankAccount}
                  onChange={(e) => handleChange("bankAccount", e.target.value)}
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="mobileMoneyNumber">
                  Mobile Money
                </FieldLabel>
                <Input
                  id="mobileMoneyNumber"
                  value={formData.mobileMoneyNumber}
                  onChange={(e) =>
                    handleChange("mobileMoneyNumber", e.target.value)
                  }
                  disabled={updateMutation.isPending}
                />
              </Field>
            </div>
          </div>

          {/* Contract Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contract Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="contractStartDate">Start Date</FieldLabel>
                <DatePicker
                  id="contractStartDate"
                  value={formData.contractStartDate}
                  onChange={(value) => handleChange("contractStartDate", value)}
                  disabled={updateMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="contractEndDate">End Date</FieldLabel>
                <DatePicker
                  id="contractEndDate"
                  value={formData.contractEndDate}
                  onChange={(value) => handleChange("contractEndDate", value)}
                  disabled={updateMutation.isPending}
                />
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
